import React, { useEffect, useState, useMemo, ChangeEvent } from 'react';

import i18n from 'i18n-js';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Button,
    InputAdornment,
    IconButton,
    ListItemIcon,
    Typography,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core';
import { alpha } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import BorderColorIcon from '@material-ui/icons/BorderColor';

import { pagingOptions } from 'constants/arrays';
import {
    CatalogDishData,
    CatalogRestaurantDishModel,
    DishCreateModel,
    PagedList,
    RestaurantCatalogDataType,
    RestaurantCatalogDataUpdateAction,
} from 'api/models';
import { useDispatch } from 'react-redux';
import agent from 'api/agent';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import { theme } from 'config/theme';
import { BasicThreeDotsMenu } from 'components/shared';
import { useStyles } from '../styles';
import SettingsBackupRestore from '@material-ui/icons/SettingsBackupRestore';
import RemoveCircleOutline from '@material-ui/icons/RemoveCircleOutline';
import { DishManagementDialog } from 'components/menu/MenuSectionManagement/components';
import menuDetailsStore from 'store/menuDetailsStore';

export default function CatalogItemsTable() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const menu = useAppSelector((state) => state.menuDetails.menu);
    const [asc, setAsc] = useState(true);
    const [orderBy, setOrderBy] = useState('name');
    const [items, setItems] = useState<PagedList<CatalogDishData>>({ list: [], count: 0 });
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[0]);
    const [page, setPage] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [selectedRestaurant, setSelectedRestaurant] = useState<string>('');
    const [catalogDishToEdit, setCatalogDishToEdit] = useState<CatalogDishData | null>(null);
    const [showEnabledOnly, setShowEnabledOnly] = useState(false);

    const restaurants = useMemo(() => {
        const uniqueRestaurants = Array.from(
            new Set(items.list.map((item) => item.restaurantName))
        );
        return uniqueRestaurants.sort();
    }, [items.list]);

    const filteredItems = useMemo(() => {
        let filtered = items.list;

        if (searchText.trim()) {
            filtered = filtered.filter((item) =>
                item.name.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        if (selectedRestaurant) {
            if (selectedRestaurant === i18n.t('navigationTabs.allRestaurants')) {
                return filtered;
            }
            filtered = filtered.filter((item) => item.restaurantName === selectedRestaurant);
        }

        return filtered;
    }, [items.list, searchText, selectedRestaurant]);

    async function fetchItems() {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            const response = await agent.Catalog.GetAllDishes(
                menu.catalogData?.catalogId!,
                page,
                rowsPerPage,
                orderBy,
                asc,
                showEnabledOnly
            );
            setItems(response);
        } catch (e) {
            dispatch(globalStore.actionCreators.showToaster('error', e));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    function handleChangePage(_event: unknown, newPage: number) {
        setPage(newPage);
    }

    function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
        const newRowsPerPage = +event.target.value;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    }

    function handleOrder(property: string) {
        const isAsc = orderBy === property && asc;
        setOrderBy(property);
        setAsc(!isAsc);
    }

    function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
        setSearchText(event.target.value);
    }

    function handleRestaurantChange(event: React.ChangeEvent<{ value: unknown }>) {
        setSelectedRestaurant(event.target.value as string);
    }

    function handleClearFilters() {
        setSearchText('');
        setSelectedRestaurant('');
        setShowEnabledOnly(false);
    }

    const hasActiveFilters =
        searchText.trim() !== '' || selectedRestaurant !== '' || showEnabledOnly;

    function renderStock(stockQuantity: number) {
        return (
            <Box
                component={'span'}
                fontWeight="bold"
                color={stockQuantity > 0 ? theme.palette.success.main : theme.palette.error.main}
            >
                {stockQuantity}
            </Box>
        );
    }

    function renderStatus(isDisabled: boolean) {
        return (
            <Box
                display="flex"
                alignItems="center"
                bgcolor={
                    isDisabled
                        ? alpha(theme.palette.error.light, 0.4)
                        : alpha(theme.palette.success.light, 0.4)
                }
                paddingX={1}
                borderRadius={50}
                height={24}
                width="fit-content"
                marginRight="auto"
            >
                <Box
                    height={5}
                    width={5}
                    borderRadius={50}
                    marginRight="10px"
                    bgcolor={isDisabled ? theme.palette.error.main : theme.palette.success.main}
                ></Box>
                <Box
                    component={'span'}
                    fontWeight="bold"
                    color={isDisabled ? theme.palette.error.main : theme.palette.success.main}
                >
                    {isDisabled ? i18n.t('common.disabled') : i18n.t('common.enabled')}
                </Box>
            </Box>
        );
    }

    function renderRestaurantName(restaurantName: string) {
        return (
            <Box
                bgcolor={alpha(theme.palette.primary.main, 0.4)}
                color={theme.palette.primary.main}
                paddingX={1}
                borderRadius={50}
                minHeight={24}
                width="fit-content"
                marginRight="auto"
                alignItems="center"
                display="flex"
            >
                <Box component={'span'} fontWeight="bold">
                    {restaurantName}
                </Box>
            </Box>
        );
    }

    async function disableCatalogDish(restaurantId: string, dishId: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            await agent.Catalog.RestaurantOverride(restaurantId, menu.catalogData?.catalogId!, {
                id: dishId,
                payload: '',
                dataType: RestaurantCatalogDataType.Dish,
                action: RestaurantCatalogDataUpdateAction.DisableEnable,
            });
            setItems((prev) => ({
                ...prev,
                list: prev.list.map((item) =>
                    item.id === dishId && item.restaurantId === restaurantId
                        ? { ...item, isDisabled: !item.isDisabled }
                        : item
                ),
            }));
            dispatch(
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('messages.updatedSuccesfully')
                )
            );
        } catch (e) {
            dispatch(globalStore.actionCreators.showToaster('error', e));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    function renderActions(dish: CatalogDishData) {
        return (
            <BasicThreeDotsMenu
                items={[
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => disableCatalogDish(dish.restaurantId, dish.id)}
                    >
                        <ListItemIcon>
                            {dish.isDisabled ? (
                                <SettingsBackupRestore fontSize="small" color="disabled" />
                            ) : (
                                <RemoveCircleOutline fontSize="small" color="disabled" />
                            )}
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {dish.isDisabled ? i18n.t('common.enable') : i18n.t('common.disable')}
                        </Typography>
                    </div>,
                    <div
                        className={classes.threeDotsMenuItemContainer}
                        onClick={() => setCatalogDishToEdit(dish)}
                    >
                        <ListItemIcon>
                            <BorderColorIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <Typography variant="inherit">{i18n.t('common.editItem')}</Typography>
                    </div>,
                ]}
            />
        );
    }

    async function updateRestaurantCatalogDish(dish: DishCreateModel) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            const response = await agent.Catalog.UpdateRestaurantCatalogDishData(
                menu.catalogData?.catalogId!,
                catalogDishToEdit!.restaurantId!,
                catalogDishToEdit!.id,
                {
                    externalId: dish.externalId,
                    price: dish.price,
                    stock: dish.stockQuantity,
                    supplierName: dish.supplierName,
                    applyToAll: false,
                    workArea: dish.categoryId,
                    priceLevels: dish.priceLevels,
                } as CatalogRestaurantDishModel
            );
            setItems((prev) => ({
                ...prev,
                list: prev.list.map((item) =>
                    item.id === response.id && item.restaurantId === response.restaurantId
                        ? response
                        : item
                ),
            }));
            dispatch(menuDetailsStore.actionCreators.restaurantMenuDetailsSetUpdatedDish(response));
            dispatch(
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('messages.updatedSuccesfully')
                )
            );
        } catch (e) {
            dispatch(globalStore.actionCreators.showToaster('error', e));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    function handleShowEnabled(_event: ChangeEvent<HTMLInputElement>, checked: boolean): void {
        setShowEnabledOnly(checked);
    }

    useEffect(() => {
        fetchItems();
    }, [page, rowsPerPage, orderBy, asc, menu.catalogData?.catalogId, showEnabledOnly]);

    return (
        <Paper
            style={{
                height: 'calc(100vh - 150px)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
                justifyContent: 'space-between',
            }}
        >
            <Box p={2} borderBottom="1px solid #e0e0e0" display="flex">
                <TextField
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 210 }}
                    placeholder={i18n.t('catalog.searchItems')}
                    value={searchText}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="disabled" />
                            </InputAdornment>
                        ),
                        endAdornment: searchText && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchText('')}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl
                    variant="outlined"
                    size="small"
                    style={{ marginLeft: 16, minWidth: 210 }}
                >
                    <InputLabel>{i18n.t('form.restaurantName')}</InputLabel>
                    <Select
                        value={selectedRestaurant}
                        onChange={handleRestaurantChange}
                        label={i18n.t('form.restaurantName')}
                    >
                        <MenuItem value={i18n.t('navigationTabs.allRestaurants')}>
                            {i18n.t('navigationTabs.allRestaurants')}
                        </MenuItem>
                        {restaurants.map((restaurant) => (
                            <MenuItem key={restaurant} value={restaurant}>
                                {restaurant}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    className={classes.mLeft15}
                    control={
                        <Checkbox
                            name="show enabled only"
                            color="primary"
                            checked={showEnabledOnly}
                            onChange={handleShowEnabled}
                        />
                    }
                    label={i18n.t('common.showEnabledOnly')}
                />
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    style={{ marginLeft: 16, minWidth: 100 }}
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters}
                    startIcon={<ClearIcon />}
                >
                    {i18n.t('catalog.clear')}
                </Button>
            </Box>
            <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                overflow="hidden"
                justifyContent="space-between"
                paddingTop="15px"
            >
                <TableContainer style={{ maxHeight: '90%' }}>
                    <Table aria-label="catalog-items-table" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === 'name'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('name')}
                                    >
                                        {i18n.t('catalog.itemName')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sortDirection={asc ? 'asc' : 'desc'}
                                    style={{ maxWidth: '200px' }}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'price'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('price')}
                                    >
                                        {i18n.t('common.price')}
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell align="left" sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === 'productId'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('productId')}
                                    >
                                        {i18n.t('form.externalId')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="left" sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === 'supplier'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('supplier')}
                                    >
                                        {i18n.t('form.supplierName')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="left" sortDirection={asc ? 'asc' : 'desc'}>
                                    <TableSortLabel
                                        active={orderBy === 'stock'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('stock')}
                                    >
                                        {i18n.t('navigationTabs.stock')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{ paddingRight: 12, paddingBottom: 16 }}
                                    sortDirection={asc ? 'asc' : 'desc'}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'status'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('status')}
                                    >
                                        {i18n.t('common.status')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{ paddingRight: 20, paddingBottom: 16 }}
                                    sortDirection={asc ? 'asc' : 'desc'}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'restaurantName'}
                                        direction={asc ? 'asc' : 'desc'}
                                        onClick={() => handleOrder('restaurantName')}
                                    >
                                        {i18n.t('form.restaurantName')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    align="left"
                                    style={{ paddingRight: 20, paddingBottom: 16 }}
                                >
                                    {i18n.t('common.actions')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredItems.length > 0 ? (
                                filteredItems.map((item, i) => (
                                    <TableRow key={item.id + i}>
                                        <TableCell align="left" style={{ paddingLeft: 28 }}>
                                            {item.name}
                                        </TableCell>
                                        <TableCell align="left" style={{ paddingLeft: 28 }}>
                                            {item.price.toFixed(2)}
                                        </TableCell>
                                        <TableCell align="left" style={{ paddingLeft: 28 }}>
                                            {item.externalId || '-'}
                                        </TableCell>
                                        <TableCell align="left" style={{ paddingLeft: 28 }}>
                                            {item.supplierName || '-'}
                                        </TableCell>
                                        <TableCell align="left" style={{ paddingLeft: 28 }}>
                                            {renderStock(item.stockQuantity)}
                                        </TableCell>
                                        <TableCell align="left" style={{ paddingLeft: 28 }}>
                                            {renderStatus(item.isDisabled)}
                                        </TableCell>
                                        <TableCell align="left" style={{ paddingRight: 20 }}>
                                            {renderRestaurantName(item.restaurantName)}
                                        </TableCell>
                                        <TableCell align="left" style={{ paddingRight: 20 }}>
                                            {renderActions(item)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={8}
                                        align="center"
                                        style={{ padding: '40px' }}
                                    >
                                        {hasActiveFilters
                                            ? i18n.t('catalog.noItemsMatchFilter')
                                            : i18n.t('catalog.noItemsFound')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={pagingOptions}
                    component="div"
                    count={items.count}
                    rowsPerPage={rowsPerPage}
                    labelRowsPerPage={i18n.t('common.rowsPerPage')}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
            {!!catalogDishToEdit && (
                <DishManagementDialog
                    dish={catalogDishToEdit!}
                    sections={menu.sections}
                    action={updateRestaurantCatalogDish}
                    isOpen={true}
                    onClose={() => setCatalogDishToEdit(null)}
                    disabled={true}
                    forCopy={false}
                    restaurantName={catalogDishToEdit.restaurantName}
                />
            )}
        </Paper>
    );
}
